export const PART_FIELDS = [
  { id: 'class_name', label: 'Klasse' },
  { id: 'subject', label: 'Fach' },
  { id: 'room', label: 'Raum' },
  { id: 'ignore', label: 'Ign' },
] as const

export type PartFieldId = (typeof PART_FIELDS)[number]['id']

export type SplitPartLookup = {
  splitFrom: string | null
  field: string | null
  compoundParent?: string | null
}

export type SplitPartInfo = {
  token: string
  id?: string
  nested: boolean
  nestedFrom?: string
  assignable: boolean
  field: string | null
  hint?: string
}

export type SplitPartGroup = {
  parent: string | null
  parts: SplitPartInfo[]
}

const PART_FIELD_SET = new Set<string>(PART_FIELDS.map((field) => field.id))

export function isPartField(field: string | null | undefined): field is PartFieldId {
  return Boolean(field && PART_FIELD_SET.has(field))
}

function parentOfMap(splits: Record<string, string[]>): Map<string, string> {
  const map = new Map<string, string>()
  for (const [parent, parts] of Object.entries(splits)) {
    for (const part of parts) {
      if (!map.has(part)) map.set(part, parent)
    }
  }
  return map
}

function immediateParent(
  token: string,
  lookup: SplitPartLookup | undefined,
  parentOf: Map<string, string>,
): string | null {
  return lookup?.splitFrom || lookup?.compoundParent || parentOf.get(token) || null
}

function rootParent(
  token: string,
  lookup: SplitPartLookup | undefined,
  parentOf: Map<string, string>,
): string | null {
  let current = immediateParent(token, lookup, parentOf)
  if (!current) return null
  const seen = new Set<string>([token])
  while (parentOf.has(current) && !seen.has(current)) {
    seen.add(current)
    current = parentOf.get(current)!
  }
  return current
}

function hintFor(token: string, nestedFrom?: string): string | undefined {
  if (nestedFrom) return `aus ${nestedFrom}`
  if (token.length === 1) return '1 Zeichen'
  return undefined
}

function walkTree(
  parent: string,
  splits: Record<string, string[]>,
): Array<{ token: string; nested: boolean; nestedFrom?: string }> {
  const parts = splits[parent]
  if (!parts?.length) return []
  const rows: Array<{ token: string; nested: boolean; nestedFrom?: string }> = []
  for (const part of parts) {
    const children = splits[part]
    if (children?.length) {
      for (const child of walkTree(part, splits)) {
        rows.push({
          token: child.token,
          nested: true,
          nestedFrom: child.nestedFrom || part,
        })
      }
    } else {
      rows.push({ token: part, nested: false })
    }
  }
  return rows
}

export function hasSplitParts(
  selected: string[],
  getLookup: (token: string) => SplitPartLookup | undefined,
  splits: Record<string, string[]>,
): boolean {
  const parentOf = parentOfMap(splits)
  return selected.some((token) => Boolean(immediateParent(token, getLookup(token), parentOf)))
}

export function groupSplitParts(
  selected: string[],
  getLookup: (token: string) => SplitPartLookup | undefined,
  splits: Record<string, string[]>,
): SplitPartGroup[] {
  const parentOf = parentOfMap(splits)
  const selectedSet = new Set(selected)
  const groups: SplitPartGroup[] = []
  const seenParents = new Set<string>()
  const groupedTokens = new Set<string>()

  const addParentGroup = (parent: string) => {
    if (seenParents.has(parent)) return
    seenParents.add(parent)
    const tree = walkTree(parent, splits)
    const parts: SplitPartInfo[] = []

    if (tree.length) {
      for (const row of tree) {
        const lookup = getLookup(row.token)
        const isSelected = selectedSet.has(row.token)
        if (!isSelected) continue
        const tokenRoot = lookup ? rootParent(row.token, lookup, parentOf) : parent
        const assignable = tokenRoot === parent && isSelected
        parts.push({
          token: row.token,
          nested: row.nested,
          nestedFrom: row.nestedFrom,
          assignable,
          field: lookup?.field ?? null,
          hint: hintFor(row.token, row.nestedFrom),
        })
        groupedTokens.add(row.token)
      }
    } else {
      for (const token of selected) {
        const lookup = getLookup(token)
        if (!lookup) continue
        if (rootParent(token, lookup, parentOf) !== parent) continue
        const imm = immediateParent(token, lookup, parentOf)
        const nested = Boolean(imm && imm !== parent)
        parts.push({
          token,
          nested,
          nestedFrom: nested ? imm || undefined : undefined,
          assignable: true,
          field: lookup.field,
          hint: hintFor(token, nested ? imm || undefined : undefined),
        })
        groupedTokens.add(token)
      }
    }

    if (parts.length) groups.push({ parent, parts })
  }

  for (const token of selected) {
    const lookup = getLookup(token)
    const root = rootParent(token, lookup, parentOf)
    if (!root) continue
    addParentGroup(root)
  }

  const rest: SplitPartInfo[] = []
  for (const token of selected) {
    if (groupedTokens.has(token)) continue
    const lookup = getLookup(token)
    rest.push({
      token,
      nested: false,
      assignable: true,
      field: lookup?.field ?? null,
      hint: hintFor(token),
    })
  }
  if (rest.length) groups.push({ parent: null, parts: rest })
  return groups
}
